/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Fragment,
  FragmentBlock,
  Heading,
  HeadingBlock,
  Image,
  ImageBlock,
  // InlineEntry,
  InlineEntryBlock,
  Link,
  LinkBlock,
  List,
  ListBlock,
  ListItem,
  ListItemBlock,
  Paragraph,
  ParagraphBlock,
  RenderBlockProps,
  RenderChildren,
  // Table,
  TableBlock
} from '@contensis/canvas-react'
import { createContext, useContext } from 'react'
import { cn } from '~/lib/utils'
import TableContent from './blocks/Tables'
import EntryContent from './blocks/Entries'
import { isDevelopment } from '~/utils/misc'

import { useLayoutItems } from '.'
import { useLayoutItems as useTableItems } from '../Content/Tables/Tables'

const ParagraphContext = createContext<null | { inLead: boolean }>(null)

export function MyFragment(props: RenderBlockProps<FragmentBlock>) {
  const paragraphContext = useContext(ParagraphContext)
  return paragraphContext?.inLead ? (
    <Fragment.Children {...props} />
  ) : (
    <Fragment {...props} />
  )
}

export function MyHeading(props: RenderBlockProps<HeadingBlock>) {
  const cssClass = `display-${props.block?.properties?.level || '1'}`
  return <Heading {...props} className={cn(cssClass, 'max-w-[80ch]')} />
  // return <Heading.Children {...props} />
}

export function MyLink(props: RenderBlockProps<LinkBlock>) {
  return <Link {...props} />
}

export function MyImage(props: RenderBlockProps<ImageBlock>) {
  const caption = props.block?.value?.caption
  const previewUrl =
    isDevelopment && !props.block?.value?.asset?.sys?.uri?.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''
  return caption ? (
    <figure className='mx-auto w-fit' style={{ display: 'block' }}>
      <Image
        {...props}
        src={previewUrl + props.block?.value?.asset?.sys?.uri}
        className='figure-img img-fluid mx-auto mt-10 mb-2 h-96'
      />
      <figcaption className='bg-grey-l p-xs-plus text-base-lg text-blue-d -mt-[1px] w-full italic'>
        {caption}
      </figcaption>
    </figure>
  ) : (
    <Image
      {...props}
      src={previewUrl + props.block.value?.asset?.sys?.uri}
      className='img-fluid'
    />
  )
}

export function MyList(props: RenderBlockProps<ListBlock>) {
  const { listType } = props?.block?.properties || {}
  const cssClass =
    listType === 'ordered'
      ? 'list-group list-group-flush list-group-numbered'
      : 'list-group list-group-flush'
  return <List {...props} className={cssClass} />
}

export function MyListItem(props: RenderBlockProps<ListItemBlock>) {
  return <ListItem {...props} className='list-group-item' />
}

export function MyParagraph(props: RenderBlockProps<ParagraphBlock>) {
  const cssClass = props.block?.properties?.paragraphType
    ? 'text-2xl border-l-[15px] pl-[30px] border-blue-l relative before:absolute before:w-[30px] before:h-[15px] before:content before:bg-blue-d before:-left-[15px] before:top-1/2 before:translate-y-1/2 before:bg-gradient-to-r before:from-white before:from-50% before:to-blue-l before:to-50%'
    : null

  const inlineEntry =
    typeof props.block?.value === 'object' &&
    props.block?.value[0]?.type === '_inlineEntry'

  if (inlineEntry) return <>{RenderChildren(props)}</>

  return (
    <ParagraphContext.Provider
      value={{ inLead: props.block?.properties?.paragraphType === 'lead' }}
    >
      <Paragraph
        {...props}
        className={cn(
          cssClass,
          'lg:mb-xs [&~:not(p)]:mt-l mb-[14px] max-w-[80ch] sm:mb-[15px]'
        )}
      />
    </ParagraphContext.Provider>
  )
}

export function MyTable(props: RenderBlockProps<TableBlock>) {
  const layoutItems = useLayoutItems()
  const tableItems = useTableItems()

  // Merge the layout items, preferring table-specific items when available
  const mergedLayoutItems = {
    ...layoutItems,
    ...tableItems,
    layoutColour: tableItems?.layoutColour || layoutItems?.layoutColour
  }

  // console.log('MyTable props:', mergedLayoutItems)

  return (
    <TableContent {...props} layoutColour={mergedLayoutItems?.layoutColour} />
  )
}

export function MyEntry(props: RenderBlockProps<InlineEntryBlock>) {
  // console.log('props', props)
  return <EntryContent {...props} />
}
